// Celebration overlay that fires when the bi-weekly Ranked Season
// rolls over and the player finished in the top 10. Triggered by the
// `season:rollover:champion` WS event that the server broadcasts at
// rollover time. Visual language matches AchievementClaimOverlay so the
// "you won something" moments across the app share an aesthetic.

import { Component } from 'preact';
import { motion } from 'framer-motion';
import {
  GOLD, ACCENT_GOLD, PARCHMENT, TEXT_PRIMARY, TEXT_MUTED,
  CONTENT_BG_DEFAULT, PANEL_BG, FourCorners, OrnamentalDivider,
} from '../lib/medievalTheme';
import { CoinIcon } from './ui/icons';
import { playUI, UI } from '../utils/arena/uiSounds';
import { t } from '../localization/t';

const PARTICLE_COUNT = 30;

// Gold / silver / bronze medal gradients for the hero medallion.
// Falls through to a brushed-gold treatment for ranks 4-10 so the
// podium reads at a glance while still feeling earned for everyone.
const MEDAL_PALETTES = {
  1: { light: '#fff2bf', mid: '#f0c84a', dark: '#7d5a16', glow: 0.6 },
  2: { light: '#f6f6f6', mid: '#cfcfcf', dark: '#7a7a7a', glow: 0.45 },
  3: { light: '#f0c08a', mid: '#c3854a', dark: '#5e3614', glow: 0.5 },
};
const DEFAULT_PALETTE = { light: '#e8d59c', mid: '#b8924a', dark: '#5e421c', glow: 0.35 };

function rankLabel(rank) {
  if (rank === 1) return t('Champion');
  if (rank === 2) return t('Runner-Up');
  if (rank === 3) return t('Third Place');
  return t('Top {n}', { n: rank });
}

export default class SeasonChampionCelebrationOverlay extends Component {
  componentDidMount() {
    // Reveal sting. Same audio cue the achievement overlay uses so
    // the moment lands with the same weight.
    playUI(UI.ACHIEVEMENT, { volume: 0.85 });
    // Payout chime after the reveal animation has had a beat to land.
    this._payoutTimer = setTimeout(() => {
      if (this._unmounted) return;
      playUI(UI.PURCHASE, { volume: 0.7, debounce: 0 });
    }, 700);
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this._payoutTimer) clearTimeout(this._payoutTimer);
  }

  handleDismiss = () => {
    if (this._unmounted) return;
    playUI(UI.CLICK);
    this.props.onDismiss?.();
  };

  handleOpenMailbox = (e) => {
    e?.stopPropagation?.();
    playUI(UI.CLICK);
    this.props.onOpenMailbox?.();
    this.props.onDismiss?.();
  };

  render() {
    const { champion } = this.props;
    if (!champion) return null;
    const { seasonId, rank, coins = 0, voucher = false, points = 0 } = champion;
    const palette = MEDAL_PALETTES[rank] || DEFAULT_PALETTE;
    const label = rankLabel(rank);
    const hasCoins = coins > 0;
    // Open Mailbox CTA only matters if there's something to claim;
    // dismiss is always available as the secondary action.
    const hasReward = hasCoins || voucher;

    return (
      <motion.div
        className="fixed inset-0 z-[300] flex items-center justify-center cursor-pointer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22 }}
        onClick={this.handleDismiss}
        style={{
          background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.88) 100%)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
      >
        <ParticleBurst />

        <motion.div
          className="relative flex flex-col items-center px-12 py-9"
          initial={{ scale: 0.7, opacity: 0, y: '2cqw' }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.94, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 220, damping: 22, mass: 0.9 }}
          style={{
            minWidth: '36cqw',
            maxWidth: '44cqw',
            background: `${CONTENT_BG_DEFAULT}, ${PANEL_BG}`,
            backgroundBlendMode: 'overlay, normal',
            border: `1px solid ${GOLD} 0.6)`,
            borderRadius: 'var(--radius-lg)',
            boxShadow: [
              '0 32px 100px rgba(0,0,0,0.75)',
              `0 0 80px ${GOLD} 0.22)`,
              `inset 0 0 0 1px ${GOLD} 0.25)`,
              `inset 0 0 60px rgba(0,0,0,0.55)`,
            ].join(', '),
            isolation: 'isolate',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FourCorners color={`${GOLD} 0.65)`} />

          {/* Header banner. Matches the achievement overlay's structure */}
          <div className="flex items-center justify-center" style={{ marginBottom: '0.6cqw' }}>
            <div
              style={{
                display: 'inline-block',
                fontSize: '0.8125cqw',
                letterSpacing: '0.5cqw',
                color: `${PARCHMENT} 0.92)`,
 fontWeight: 700,
 padding: '0.3cqw 1.5cqw',
 borderTop: `1px solid ${GOLD} 0.45)`,
                borderBottom: `1px solid ${GOLD} 0.45)`,
                background: `linear-gradient(180deg, transparent, ${GOLD} 0.08), transparent)`,
                textShadow: '0 1px 2px rgba(0,0,0,0.7)',
              }}
            >
              {t('Season {n} Standings', { n: seasonId })}
            </div>
          </div>

          <OrnamentalDivider className="w-full mt-1 mb-3" />

          {/* Hero medallion. The rank number in a struck-coin face */}
          <div
            className="relative flex items-center justify-center"
            style={{ width: '7.5cqw', height: '7.5cqw', marginTop: '0.5cqw' }}
          >
            <motion.div
              className="absolute inset-0 rounded-full pointer-events-none"
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: [1, 1.22, 1], opacity: [0.55, 0.22, 0.55] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                background: `radial-gradient(circle, ${palette.mid}AA 0%, ${palette.mid}00 70%)`,
                filter: 'blur(10px)',
              }}
            />
            <motion.div
              className="relative rounded-full overflow-hidden flex items-center justify-center"
              initial={{ rotate: -10, scale: 0.82 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ duration: 0.55, ease: [0.18, 0.9, 0.32, 1] }}
              style={{
                width: '6.25cqw',
                height: '6.25cqw',
                background: `radial-gradient(circle at 35% 28%, ${palette.light} 0%, ${palette.mid} 55%, ${palette.dark} 100%)`,
                border: `1.5px solid ${ACCENT_GOLD}`,
                boxShadow: [
                  `inset 0 0 0 2px ${GOLD} 0.35)`,
                  `inset 0 2px 4px rgba(255, 220, 140, 0.28)`,
                  `inset 0 -3px 6px rgba(0, 0, 0, 0.5)`,
                  `0 0 28px ${palette.mid}${Math.round(palette.glow * 255).toString(16).padStart(2, '0')}`,
                ].join(', '),
                color: 'rgba(30, 22, 8, 0.92)',
                fontWeight: 800,
                fontSize: '2.5cqw',
                textShadow: '0 1px 0 rgba(255, 235, 195, 0.45)',
                isolation: 'isolate',
              }}
            >
              {rank}
            </motion.div>
          </div>

          {/* Headline + summary */}
          <div
            className="flex flex-col items-center text-center"
            style={{ maxWidth: '30cqw', marginTop: '1.25cqw', gap: '0.5cqw' }}
          >
            <h2
              className="arena-heading font-bold"
              style={{
                fontSize: '1.75cqw',
                letterSpacing: '0.04em',
                lineHeight: 1.1,
                background: 'linear-gradient(180deg, #fbe9b8 0%, #e8c466 55%, #b9893a 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.7))',
              }}
            >
              {label}
            </h2>
            <p
              className="text-[0.8125cqw]"
              style={{ color: `${PARCHMENT} 0.78)`, lineHeight: 1.45, fontStyle: 'italic' }}
            >
              {t('{points} valor this season. Your reward is waiting in your inbox.', { points: `${points >= 0 ? '+' : ''}${points}` })}
            </p>
          </div>

          <OrnamentalDivider className="w-full mt-4 mb-3" />

          {/* Reward pill(s). Coins pill for top-3, voucher pill for
              top-10. When the player gets both, they stack side-by-side
              so the wins read at a glance. */}
          <div className="flex items-center justify-center gap-2 flex-wrap" style={{ minHeight: '2.75cqw' }}>
            {hasCoins ? (
              <motion.div
                key="coin-pill"
                initial={{ scale: 0.4, opacity: 0, y: 12 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 320, damping: 18, delay: 0.7 }}
                className="flex items-center gap-2.5 px-5 py-1.5 rounded-full"
                style={{
                  background: 'linear-gradient(180deg, rgba(34, 26, 14, 0.99) 0%, rgba(16, 12, 6, 0.99) 100%)',
                  border: `1.5px solid ${ACCENT_GOLD}`,
                  boxShadow: [
                    `0 0 22px ${GOLD} 0.45)`,
                    'inset 0 1px 0 rgba(255, 220, 140, 0.32)',
                    'inset 0 -2px 4px rgba(0, 0, 0, 0.55)',
                  ].join(', '),
                }}
              >
                <CoinIcon size={18} />
                <span
                  className="text-[1.125cqw] font-bold tabular-nums"
                  style={{
                    background: 'linear-gradient(180deg, #fbe9b8 0%, #e8c466 60%, #b9893a 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
                  }}
                >
                  +{coins.toLocaleString()}
                </span>
                <span
                  className="text-[0.625cqw] font-semibold"
                  style={{ color: '#c9a86a' }}
                >
                  {t('Coins')}
                </span>
              </motion.div>
            ) : null}

            {voucher ? (
              <motion.div
                key="voucher-pill"
                initial={{ scale: 0.4, opacity: 0, y: 12 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 320, damping: 18, delay: hasCoins ? 0.95 : 0.7 }}
                className="flex items-center gap-2.5 px-5 py-1.5 rounded-full"
                style={{
                  background: 'linear-gradient(180deg, rgba(34, 26, 14, 0.99) 0%, rgba(16, 12, 6, 0.99) 100%)',
                  border: `1.5px solid ${ACCENT_GOLD}`,
                  boxShadow: [
                    `0 0 22px ${GOLD} 0.45)`,
                    'inset 0 1px 0 rgba(255, 220, 140, 0.32)',
                    'inset 0 -2px 4px rgba(0, 0, 0, 0.55)',
                  ].join(', '),
                }}
              >
                {/* Portrait frame glyph. A small framed portrait
                    icon evokes the cosmetic the voucher unlocks. */}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  style={{ width: '1cqw', height: '1cqw', color: ACCENT_GOLD }}
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="12" cy="10" r="3" />
                  <path d="M6 19c1.5-3 4-4.5 6-4.5s4.5 1.5 6 4.5" />
                </svg>
                <span
                  className="text-[0.875cqw] font-bold"
                  style={{
                    background: 'linear-gradient(180deg, #fbe9b8 0%, #e8c466 60%, #b9893a 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
                  }}
                >
                  {t('Portrait Pack')}
                </span>
                <span
                  className="text-[0.625cqw] font-semibold"
                  style={{ color: '#c9a86a' }}
                >
                  {t('Free')}
                </span>
              </motion.div>
            ) : null}
          </div>

          {/* CTAs. Open Mailbox (primary) + Continue (secondary). */}
          <div className="flex items-center gap-3 mt-4">
            {hasReward && this.props.onOpenMailbox ? (
              <button
                type="button"
                onClick={this.handleOpenMailbox}
                className="cursor-pointer transition-all"
                style={{
                  padding: '0.55cqw 1.75cqw',
                  background: 'linear-gradient(180deg, rgba(48, 36, 16, 1) 0%, rgba(28, 20, 8, 1) 100%)',
                  border: `1.5px solid ${GOLD} 0.8)`,
 borderRadius: 'var(--radius-md)',
 color: ACCENT_GOLD,
 fontSize: '0.75cqw',
 fontWeight: 700,
 textShadow: `0 0 10px ${GOLD} 0.4)`,
                  boxShadow: [
                    `0 0 18px ${GOLD} 0.3)`,
                    'inset 0 1px 0 rgba(255, 220, 140, 0.22)',
                    'inset 0 -2px 4px rgba(0, 0, 0, 0.55)',
                  ].join(', '),
                }}
              >
                {t('Open Mailbox')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={this.handleDismiss}
              className="cursor-pointer transition-all"
              style={{
                padding: '0.55cqw 1.75cqw',
                background: 'linear-gradient(180deg, rgba(28, 22, 14, 0.99) 0%, rgba(14, 10, 6, 0.99) 100%)',
                border: `1.5px solid ${GOLD} 0.4)`,
 borderRadius: 'var(--radius-md)',
 color: TEXT_MUTED,
 fontSize: '0.75cqw',
 fontWeight: 700,
 boxShadow: 'inset 0 -2px 4px rgba(0, 0, 0, 0.45)',
 }}
 >
 {t('Continue')}
 </button>
 </div>
 </motion.div>
 </motion.div>
 );
 }
}

function ParticleBurst() {
 const motes = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
 const angle = (i / PARTICLE_COUNT) * Math.PI * 2 + Math.random() * 0.4;
 const distance = 8 + Math.random() * 7;
 const x = Math.cos(angle) * distance;
 const y = Math.sin(angle) * distance;
 const delay = Math.random() * 0.18;
 return { x, y, delay, key: i };
 });
 return (
 <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
 {motes.map((m) => (
 <motion.div
 key={m.key}
 className="absolute rounded-full"
 initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
 animate={{ x: `${m.x}cqw`, y: `${m.y}cqw`, opacity: 0, scale: 0.25 }}
          transition={{ duration: 1.35, delay: m.delay, ease: [0.22, 0.8, 0.36, 1] }}
          style={{
            width: '0.5cqw',
            height: '0.5cqw',
            background: ACCENT_GOLD,
            boxShadow: `0 0 10px ${GOLD} 0.85)`,
          }}
        />
      ))}
    </div>
  );
}
